import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import {
  SITE_SETTING_KEYS,
  HERO_IMAGE_META,
  FINISHING_THUMBNAIL_META,
  CATEGORY_IMAGE_META,
} from '@/lib/siteSettings';
import ImageUploadField from './components/ImageUploadField';
import NoticeManager from './components/NoticeManager';
import CompanyInfoManager from './components/CompanyInfoManager';

export type ContentSection =
  | 'notice'
  | 'company'
  | 'hero'
  | 'cta'
  | 'finishing'
  | 'category';

export default function ContentSectionPage({ section }: { section: ContentSection }) {
  const { user, isAdmin, isLoading: authLoading, isProfileLoading } = useAuth();
  const {
    settings,
    loading,
    error,
    saving,
    setSetting,
    removeSetting,
    uploadImage,
    reload,
  } = useSiteSettings();

  const canEdit = isAdmin;

  const buildIdUrlArray = (overrides: Record<string, string>) =>
    Object.entries(overrides).map(([id, url]) => ({ id, url }));

  const saveHeroImage = async (id: string, file: File) => {
    const url = await uploadImage(file);
    const next = { ...settings.homeHeroImages, [id]: url };
    await setSetting(SITE_SETTING_KEYS.homeHeroImages, buildIdUrlArray(next));
  };

  const resetHeroImage = async (id: string) => {
    const next = { ...settings.homeHeroImages };
    delete next[id];
    await setSetting(SITE_SETTING_KEYS.homeHeroImages, buildIdUrlArray(next));
  };

  const saveCtaImage = async (file: File) => {
    const url = await uploadImage(file);
    await setSetting(SITE_SETTING_KEYS.homeCtaImage, url);
  };

  const resetCtaImage = async () => {
    await removeSetting(SITE_SETTING_KEYS.homeCtaImage);
  };

  const saveFinishingThumbnail = async (id: string, file: File) => {
    const url = await uploadImage(file);
    const next = { ...settings.productFinishingThumbnails, [id]: url };
    await setSetting(
      SITE_SETTING_KEYS.productFinishingThumbnails,
      buildIdUrlArray(next),
    );
  };

  const resetFinishingThumbnail = async (id: string) => {
    const next = { ...settings.productFinishingThumbnails };
    delete next[id];
    await setSetting(
      SITE_SETTING_KEYS.productFinishingThumbnails,
      buildIdUrlArray(next),
    );
  };

  const saveCategoryImage = async (id: string, file: File) => {
    const url = await uploadImage(file);
    const next = { ...settings.homeCategoryImages, [id]: url };
    await setSetting(SITE_SETTING_KEYS.homeCategoryImages, buildIdUrlArray(next));
  };

  const resetCategoryImage = async (id: string) => {
    const next = { ...settings.homeCategoryImages };
    delete next[id];
    await setSetting(SITE_SETTING_KEYS.homeCategoryImages, buildIdUrlArray(next));
  };

  const ctaUrl = settings.homeCtaImage || '';

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 pb-14">
      <div className="max-w-6xl mx-auto">
        {/* Auth warning */}
        {!authLoading && !isProfileLoading && !canEdit && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <i className="ri-lock-line text-amber-600 text-lg" />
            <p className="text-sm text-amber-800 flex-1">
              이미지를 수정하려면 관리자 계정으로 로그인해야 해요. 지금은 조회만
              가능해요.
            </p>
            {!user ? (
              <a
                href="/sign-in"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors whitespace-nowrap"
              >
                로그인
              </a>
            ) : (
              <span className="text-xs text-amber-700 font-medium">
                현재 계정: {user.email}
              </span>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center gap-3">
            <i className="ri-error-warning-line text-red-600 text-lg" />
            <p className="text-sm text-red-700 flex-1">
              설정을 불러오지 못했어요: {error}
            </p>
            <button
              type="button"
              onClick={() => reload()}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            <span className="text-sm">불러오는 중...</span>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <div className="space-y-6">
            {section === 'notice' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    상단 공지
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    홈페이지 상단에 표시되는 공지사항을 추가, 수정, 삭제할 수
                    있어요. 활성화된 공지만 표시돼요.
                  </p>
                </div>
                <NoticeManager canEdit={canEdit} />
              </>
            )}

            {section === 'company' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    회사정보
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    사이트 하단 회사정보 섹션에 표시되는 이름, 연락처, 주소예요.
                    수정하면 전체 사이트에 반영돼요.
                  </p>
                </div>
                <CompanyInfoManager canEdit={canEdit} />
              </>
            )}

            {section === 'hero' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    홈 히어로 배경
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    홈페이지 맨 위 히어로 영역에서 자동으로 스크롤되며 번갈아
                    표시되는 4장의 배경 이미지예요. 넓은 가로형 이미지를
                    권장해요.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {HERO_IMAGE_META.map((meta) => (
                    <ImageUploadField
                      key={meta.id}
                      label={meta.label}
                      description="가로 1600px 이상의 이미지를 올리면 가장 깔끔해요."
                      imageUrl={settings.homeHeroImages[meta.id] || ''}
                      isUnset={!settings.homeHeroImages[meta.id]}
                      disabled={!canEdit || saving}
                      onUpload={(file) => saveHeroImage(meta.id, file)}
                      onReset={() => resetHeroImage(meta.id)}
                      previewClassName="h-40"
                    />
                  ))}
                </div>
              </>
            )}

            {section === 'cta' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    CTA 섹션 배경
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    '기업 솔루션' 안내 섹션의 오른쪽에 표시되는 물류/항만 배경
                    이미지예요.
                  </p>
                </div>
                <ImageUploadField
                  label="CTA 배경 이미지"
                  imageUrl={ctaUrl}
                  isUnset={!settings.homeCtaImage}
                  disabled={!canEdit || saving}
                  onUpload={saveCtaImage}
                  onReset={resetCtaImage}
                  previewClassName="h-52 sm:h-64"
                />
              </>
            )}

            {section === 'finishing' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    제품 상세 후가공 썸네일
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    제품 상세 페이지의 'AI 후가공 이미지' 미리보기에 표시되는
                    5가지 후가공 샘플이에요. 설정하지 않으면 아이콘으로
                    표시돼요.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                  {FINISHING_THUMBNAIL_META.map((meta) => (
                    <ImageUploadField
                      key={meta.id}
                      label={meta.label}
                      imageUrl={
                        settings.productFinishingThumbnails[meta.id] || ''
                      }
                      isUnset={!settings.productFinishingThumbnails[meta.id]}
                      disabled={!canEdit || saving}
                      onUpload={(file) =>
                        saveFinishingThumbnail(meta.id, file)
                      }
                      onReset={() => resetFinishingThumbnail(meta.id)}
                      previewClassName="h-40"
                    />
                  ))}
                </div>
              </>
            )}

            {section === 'category' && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-stone-900">
                    카테고리 이미지
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    홈페이지 '카테고리' 섹션에서 각 카테고리 박스에 표시되는
                    대표 이미지예요. 가로형(4:3) 이미지를 권장해요. 설정하지
                    않으면 제품에 등록된 이미지나 기본 이미지가 자동으로
                    표시돼요.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {CATEGORY_IMAGE_META.map((meta) => (
                    <ImageUploadField
                      key={meta.id}
                      label={meta.label}
                      imageUrl={settings.homeCategoryImages[meta.id] || ''}
                      isUnset={!settings.homeCategoryImages[meta.id]}
                      disabled={!canEdit || saving}
                      onUpload={(file) => saveCategoryImage(meta.id, file)}
                      onReset={() => resetCategoryImage(meta.id)}
                      previewClassName="h-40"
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}